"use client";

import { useState, useCallback, useEffect } from "react";

type ToastVariant = "default" | "destructive" | "success";

interface Toast {
  id?: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function Toast({ toast, onRemove }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (toast.id) onRemove(toast.id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  const variantStyles = {
    default: "bg-background border border-border",
    destructive: "bg-red-50 border border-red-200 text-red-800",
    success: "bg-green-50 border border-green-200 text-green-800",
  };

  return (
    <div
      role={toast.variant === "destructive" ? "alert" : "status"}
      aria-live={toast.variant === "destructive" ? "assertive" : "polite"}
      aria-atomic="true"
      className={`fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg touch-none ${variantStyles[toast.variant || "default"]}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-semibold text-sm">{toast.title}</h4>
          {toast.description && <p className="text-sm opacity-90 mt-1">{toast.description}</p>}
        </div>
        <button
          onClick={() => toast.id && onRemove(toast.id)}
          className="ml-4 text-lg leading-none opacity-70 hover:opacity-100"
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((newToast: Toast) => {
    const id = Date.now().toString();
    const toastWithId = { ...newToast, id };
    setToasts((prev) => [...prev, toastWithId]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toast, toasts, removeToast };
}

export function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: Toast[];
  removeToast: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-0 right-0 z-50 p-4 space-y-2 touch-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
}
